// src/components/onboarding/postWonSteps.js
//
// THE ONE MAP for "Post Won Proposal Steps": step id, display letter, label,
// backend stage code, blurb. React-free and import-light (onboardingRecord
// only, itself import-free) so it can be unit-tested without MSAL and read by
// every surface that names a step — the panel, the modals, the chips.
//
// ⛔ RENAME THE LABELS, NEVER THE CODES. The letters A/B/C the BACKEND uses are
// not UI text: they are the wire protocol (`POST /api/onboarding/run` takes
// `stages: ["B","C"]`, validated against VALID_STAGES) and PERSISTED DATA
// (every error is `{ stage, message, at }` in record.errors, and
// `withoutStage(errors, "B")` clears by that letter on re-run). Renaming the
// stored letter would silently re-point every historic error. So the backend
// keeps A/B/C forever and this module owns ONE display map — remap-on-read,
// exactly as onboardingService already corrects a mis-tagged error as it is
// read rather than rewriting storage.
//
//   shown  label                              backend  where it runs
//   A      Contract to the client             —        ContractsPanel (not a backend stage)
//   B      Contracts for Autotask             A        /run stages:["A"]
//   C      Tickets                            B        /run stages:["B"]
//   D      Onboarding ticket & checklist      C        /run stages:["C"]
//   E      Quotes from the proposal           —        local state only, never the backend
//
// B→C→D is the order the backend already enforces ("the contract must exist
// before tickets reference the work it pays for"); E is the exception — no
// backend dependency, can be run any time — and is shown as such.
//
// ⛔ THE "NOTHING TO DO" FLAG IS KEYED BY STEP ID, NEVER BY A LETTER. A letter
// has just proved it can be renumbered. `record.skipped[id]` is an ISO string
// when ticked, null when not; absent on every existing record and never
// backfilled — read defensively. Skipped is NOT done, is reversible in one
// click, and never blocks a real run: running a stage clears its flag.
import { contractsFor, onboardingRecordOf } from "./onboardingRecord";

export const POST_WON_STEPS = [
  {
    id: "client_contract", letter: "A", label: "Contract to the client", backendCode: null, runsIn: "contracts",
    blurb: "Send the contract for signature, or record one that was signed elsewhere. Its signature date becomes the Autotask contract's start date.",
  },
  {
    id: "at_contract", letter: "B", label: "Contracts for Autotask", backendCode: "A", runsIn: "backend",
    blurb: "The recurring-services contract and its service lines in Autotask. Writes a financial record — review it first.",
  },
  {
    id: "tickets", letter: "C", label: "Tickets", backendCode: "B", runsIn: "backend",
    blurb: "One project ticket per foundational block, plus Intune, Copper and Bronze tickets where the proposal calls for them, each with its own fixed-price contract.",
  },
  {
    id: "onboarding_tkt", letter: "D", label: "Onboarding ticket & checklist", backendCode: "C", runsIn: "backend",
    blurb: "The onboarding ticket, its contract, and the tier's checklist library — the attach is reported as an outcome, it cannot be read back.",
  },
  {
    id: "quotes", letter: "E", label: "Quotes from the proposal", backendCode: null, runsIn: "local",
    blurb: "One quote per foundational block, in this app only — nothing goes to Autotask. Independent of the steps above; run it any time.",
  },
];

export const STEP_IDS = POST_WON_STEPS.map((s) => s.id);
export const stepById = (id) => POST_WON_STEPS.find((s) => s.id === id) || null;
export const stepForBackendCode = (code) => POST_WON_STEPS.find((s) => s.backendCode === code) || null;
/** "Stage B — Contracts for Autotask" for a stored error tagged "A". */
export const displayNameForBackendCode = (code) => {
  const s = stepForBackendCode(code);
  return s ? `Stage ${s.letter} — ${s.label}` : `Stage ${code}`;
};
export const stepTitle = (step) => `Stage ${step.letter} — ${step.label}`;

/* Contract instance statuses that mean Step A has left the building. A draft
   instance (created, never sent) does not; declined / voided do not. */
export const CLIENT_CONTRACT_DONE_STATUSES = ["sent", "delivered", "signed"];

/* ── The "nothing to do" flag ─────────────────────────────────────────────── */

export const emptySkipped = () => Object.fromEntries(STEP_IDS.map((id) => [id, null]));

/** ISO string when ticked, else null. Defensive: the field is absent on old records. */
export const skippedAt = (record, id) => (record && record.skipped && record.skipped[id]) || null;

/** The proposal patch that toggles one step's flag. The record lives on the PROPOSAL. */
export function toggleSkippedPatch(record, id, now = new Date().toISOString()) {
  const base = record || {};
  const skipped = { ...emptySkipped(), ...(base.skipped || {}) };
  skipped[id] = skipped[id] ? null : now;
  return { onboarding: { ...base, skipped } };
}

/** The record with the flags cleared for the steps a run just covered —
 *  a skipped step that was then run for real is no longer "nothing to do". */
export function clearSkippedForStages(record, backendCodes = []) {
  if (!record || !record.skipped) return record;
  const ids = backendCodes.map((c) => stepForBackendCode(c)?.id).filter(Boolean);
  if (!ids.some((id) => record.skipped[id])) return record;
  const skipped = { ...record.skipped };
  ids.forEach((id) => { skipped[id] = null; });
  return { ...record, skipped };
}

/* ── Each step's state ────────────────────────────────────────────────────── */

/**
 * One of: not_started · done · skipped · errors, plus a one-line detail.
 * Precedence: a live error for the step wins (something is wrong even if part
 * of it landed); then a real completion (never masked by a skip flag); then
 * the skip flag; else not started.
 *
 * @param ctx { proposal, contractInstances, record?, plannedTickets?, quoteCount? }
 */
export function stepState(id, ctx = {}) {
  const { proposal = null, contractInstances = [], plannedTickets = null, quoteCount = 0 } = ctx;
  const record = ctx.record !== undefined ? ctx.record : onboardingRecordOf(proposal, contractInstances);
  const step = stepById(id);
  const errors = step && step.backendCode
    ? ((record && record.errors) || []).filter((e) => e && e.stage === step.backendCode)
    : [];
  const skipped = skippedAt(record, id);
  const out = (state, detail, extra = {}) => ({ id, state, detail, errors, skippedAt: skipped, ...extra });

  if (id === "client_contract") {
    const all = contractsFor(contractInstances, proposal?.id);
    const live = all.filter((c) => CLIENT_CONTRACT_DONE_STATUSES.includes(c.status));
    const signed = all.filter((c) => c.status === "signed");
    if (live.length) {
      return out("done", signed.length ? `${signed.length} signed` : `${live.length} sent, awaiting signature`, { signed: signed.length > 0 });
    }
    if (skipped) return out("skipped", "Marked nothing to do");
    return out("not_started", all.length ? `${all.length} draft${all.length === 1 ? "" : "s"}, nothing sent yet` : "No contract yet");
  }
  if (id === "at_contract") {
    const done = !!(record && record.autotaskContractId);
    if (errors.length) return out("errors", done ? `Contract #${record.autotaskContractId} created; ${errors.length} problem${errors.length === 1 ? "" : "s"}` : `${errors.length} problem${errors.length === 1 ? "" : "s"}`);
    if (done) {
      const lines = record.contractServiceIds || [];
      const adjusted = lines.filter((l) => l.adjusted).length;
      return out("done", `Contract #${record.autotaskContractId}${lines.length ? ` · ${adjusted}/${lines.length} lines with quantities` : ""}`);
    }
    if (skipped) return out("skipped", "Marked nothing to do");
    return out("not_started", "Not created");
  }
  if (id === "tickets") {
    const have = ((record && record.ticketIds) || []).length;
    const planned = plannedTickets == null ? null : plannedTickets;
    const count = planned == null ? `${have} created` : `${have}/${planned} created`;
    if (errors.length) return out("errors", `${count} · ${errors.length} problem${errors.length === 1 ? "" : "s"}`);
    if (have > 0) return out("done", count, { partial: planned != null && have < planned });
    if (skipped) return out("skipped", "Marked nothing to do");
    return out("not_started", planned == null ? "Not created" : `0/${planned} created`);
  }
  if (id === "onboarding_tkt") {
    const done = !!(record && record.onboardingTicketId);
    const attach = !record ? null : record.checklistAttached === true ? "checklist attach reported OK (cannot be read back)" : "checklist NOT attached";
    const num = record && (record.onboardingTicketNumber || record.onboardingTicketId);
    if (errors.length) return out("errors", done ? `Ticket ${num} · ${attach} · ${errors.length} problem${errors.length === 1 ? "" : "s"}` : `${errors.length} problem${errors.length === 1 ? "" : "s"}`);
    if (done) return out("done", `Ticket ${num} · ${attach}`, { checklistAttached: record.checklistAttached === true });
    if (skipped) return out("skipped", "Marked nothing to do");
    return out("not_started", "Not created");
  }
  if (id === "quotes") {
    if (quoteCount > 0) return out("done", `${quoteCount} quote${quoteCount === 1 ? "" : "s"}`);
    if (skipped) return out("skipped", "Marked nothing to do");
    return out("not_started", "No quotes yet");
  }
  return out("not_started", "");
}

/** Errors in a record that no step claims (an unknown stage letter) — shown, never dropped. */
export const unmappedErrors = (record) =>
  ((record && record.errors) || []).filter((e) => !e || !stepForBackendCode(e.stage));
